import React, { useState, useRef, useEffect, useCallback, useContext, useMemo } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useMusicPlayer } from '../MusicPlayerContext';
import { shareMusic, copyToClipboard } from '../utils/shareMusic';
import { downloadMusic } from '../utils/downloadMusic';
import { cacheMusicMetadata, cacheMusicFile, getCachedMusicMetadata, isFileCached } from '../utils/musicCache';
import { showUiHint, showHintPlus } from '../utils/uiHint';
import { getMusicStreamUrl, getLyricsStreamUrl, getCoverStreamUrl } from '../utils/musicStreamUrls';
import { isInstrumentalMusic } from '../utils/musicMeta';
import { detectWasmSimdSupport } from '../utils/wasmSimd';
import { AdminContext } from '../AdminContext';
import { useUser } from '../UserContext';
import { spaNavigate } from '../utils/spaNavigate';
import { getVideoFilePath, formatTime, getActiveMediaElement } from './musicPlayer/utils';
import { useAutoHideOnStop } from './musicPlayer/useAutoHideOnStop';
import PlayerPortals from './musicPlayer/PlayerPortals';
import { formatBitrate as formatBitrateRaw, formatSampleRate as formatSampleRateRaw, formatFileSize as formatFileSizeRaw } from './musicPlayer/mediaFormatters';
import { getCoverUrlFromMusic as getCoverUrlFromMusicHelper, getNextMusicForCover as getNextMusicForCoverHelper } from './musicPlayer/helpers/coverHelpers';
import { getDefaultTranslationMap, resolveTargetTranslationLang as resolveTargetTranslationLangHelper, getCurrentLanguageTitle as getCurrentLanguageTitleHelper } from './musicPlayer/helpers/translationHelpers';
import { useBreakpoint } from './musicPlayer/hooks/useBreakpoint';
import { useLyricsViewportHeight } from './musicPlayer/hooks/useLyricsViewportHeight';
import { useVideoWindowVisibleSync } from './musicPlayer/hooks/useVideoWindowVisibleSync';
import SeekPreviewPortal from './musicPlayer/SeekPreviewPortal';
import DockBar from './musicPlayer/DockBar';
import { useWakeLock } from './musicPlayer/hooks/useWakeLock';
import { useDesktopControlsOverflow } from './musicPlayer/hooks/useDesktopControlsOverflow';
import { useSurroundFreeTrial } from './musicPlayer/hooks/useSurroundFreeTrial';
import { useExternalPlayingSync } from './musicPlayer/hooks/useExternalPlayingSync';
import { useCacheStatusOnMusic } from './musicPlayer/hooks/useCacheStatusOnMusic';
import { usePlayerPlaylistHandlers } from './musicPlayer/hooks/usePlayerPlaylistHandlers';
import { useSleepTimer } from './musicPlayer/hooks/useSleepTimer';
import { ensureVideoMediaInfo as ensureVideoMediaInfoHelper } from './musicPlayer/helpers/mediaInfo';
import { startPlayHistoryTracking as startPlayHistoryTrackingHelper, stopPlayHistoryTracking as stopPlayHistoryTrackingHelper, recordPlay as recordPlayHelper, recordPlayProgress as recordPlayProgressHelper } from './musicPlayer/helpers/playbackHistory';
import { usePlayerHeightCssVar } from './musicPlayer/usePlayerHeightCssVar';
import { useVideoDocking } from './musicPlayer/useVideoDocking';
import ShareDialog from './ShareDialog';
import { getShareMeta, mergeShareMeta } from '../utils/getShareMeta';
import { mpLog, mpWarn, logSimdOnPlay, FADE_DURATION_MS, FADE_TICK_MS, getFadeStep, ZH_CN, ZH_TW, ZH_CN_LC, ZH_TW_LC, PLAY_HISTORY_INTERVAL_MS, AUTO_HIDE_DELAY_MS } from './musicPlayer/constants';
import { loadPlayerSettings, savePlayerSettings, buildSettingsFromState } from './musicPlayer/playerSettings';
import { useTuacDefaults } from './musicPlayer/TuacDefaultsContext';
import { useLyricsSync } from './musicPlayer/useLyricsSync';
import { usePlayerGestures } from './musicPlayer/usePlayerGestures';
import { useCoverLayers } from './musicPlayer/useCoverLayers';
import { useLoadMusic } from './musicPlayer/useLoadMusic';
import { useProgressTracking } from './musicPlayer/useProgressTracking';
import { getHighlightThemeClasses, getGlowAlpha } from './musicPlayer/lyricHighlightStyles';
import { getUserId } from '../utils/getUserId';
import { runFadeIn } from './musicPlayer/fadeUtils';
import { loadLyricsWithTranslation } from './musicPlayer/lyricsLoader';
import { useMediaSession } from './musicPlayer/useMediaSession';
import { useAudioOutput } from './musicPlayer/useAudioOutput';
import { useVideoSubtitleScale } from './musicPlayer/useVideoSubtitleScale';
import { createHandleEnded } from './musicPlayer/endedHandler';
import { setupIosShadowAudio } from './musicPlayer/iosShadowAudio';
import { useAudioGraph } from './musicPlayer/useAudioGraph';
import AudioQualityMonitor from './musicPlayer/audioQualityMonitor';
import { PlayerPanelContext } from './musicPlayer/PlayerPanelContext';

const MusicPlayer = ({ 
  currentMusic = null, 
  playlist = [], 
  playing: externalPlaying = null,
  onNext = null, 
  onPrevious = null,
  onPlaylistChange = null,
  onRemoveFromPlaylist = null
}) => {
  const { i18n, t } = useTranslation();
  const {
    setPlaying: setContextPlaying,
    showPlaylist: contextShowPlaylist,
    setShowPlaylist: setContextShowPlaylist,
    setCurrentMusic: setContextCurrentMusic,
    setPlaylist: setContextPlaylist,
    addToPlaylist,
    removeFromPlaylist: contextRemoveFromPlaylist
  } = useMusicPlayer();
  
  // 使用 Context 中的 setPlaylist，如果没有则使用内部处理
  const handleSetPlaylist = setContextPlaylist || ((newPlaylist) => {
    // 如果没有 Context，尝试通过 onPlaylistChange 通知父组件
    // 这里只是兜底，实际应该通过 Context 管理
  });

  // 处理移除播放列表项：优先使用 Context 中的，否则使用 props 中的
  const handleRemoveFromPlaylist = useCallback((musicOrId) => {
    // 如果是 music 对象，提取 id
    const musicId = typeof musicOrId === 'object' && musicOrId?.id ? musicOrId.id : musicOrId;
    
    // 优先使用 Context 中的 removeFromPlaylist
    if (contextRemoveFromPlaylist) {
      contextRemoveFromPlaylist(musicId);
    } 
    // 否则使用 props 中的 onRemoveFromPlaylist
    else if (onRemoveFromPlaylist) {
      onRemoveFromPlaylist(musicOrId);
    }
  }, [contextRemoveFromPlaylist, onRemoveFromPlaylist]);
  const { isAdmin } = useContext(AdminContext);
  const playerRef = useRef(null);
  const playlistPanelRef = useRef(null);
  const settingsPanelRef = useRef(null);
  const settingsButtonRef = useRef(null);
  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const audioContextRef = useRef(null);
  const wasmDecodeAbortRef = useRef(0);
  const wasmAudioUrlRef = useRef(null);
  const lockInterruptionRef = useRef({ wasPlaying: false });
  const iosShadowAudioRef = useRef(false);
  const mediaSourceRefs = useRef({ audio: null, video: null });
  const fidelityRef = useRef(null);
  const tuacEngineRef = useRef(null);
  const bassShelfRef = useRef(null);
  const dryGainRef = useRef(null);
  const wetGainRef = useRef(null);
  const mergeRef = useRef(null);
  const analyserRef = useRef(null); // 频谱分析：接在 Audio Out 之前
  
  // ========== 音频质量监控系统 ==========
  const audioQualityMonitorRef = useRef(null);
  const [tuacAutoDisabled, setTuacAutoDisabled] = useState(false);
  const hasMonitoredCurrentSong = useRef(false);
  const activeMediaTypeRef = useRef(null);
  const wakeLockRef = useRef(null); // Wake Lock API：防止屏幕锁定（仅在未手动锁屏时生效）
  const progressIntervalRef = useRef(null);
  const playHistoryIntervalRef = useRef(null);
  const progressRafRef = useRef(null); // 用 rAF 更新进度条 DOM，避免高频 setState 卡顿
  const fadeOutTimerRef = useRef(null);
  const userInitiatedChangeRef = useRef(false);
  const lastLoadedMusicIdRef = useRef(null);
  const pendingVideoLoadRef = useRef(null);
  const progressTrackRef = useRef(null);
  const progressFillRef = useRef(null);
  const progressKnobRef = useRef(null);
  const seekPreviewRef = useRef(null);
  const seekPreviewTimeRef = useRef(null);
  const seekPreviewTextRef = useRef(null);
  const seekPreviewTransRef = useRef(null);
  const seekPreviewTextAnimRef = useRef(null);
  const seekPreviewTransAnimRef = useRef(null);
  const seekPreviewRafRef = useRef(null);
  const seekPreviewXRef = useRef(0);
  const seekPreviewTargetXRef = useRef(0);
  const seekPreviewVisibleRef = useRef(false);
  const seekPreviewHideTimerRef = useRef(null);
  const seekPreviewLastTargetXRef = useRef(0);
  const seekPreviewLastTsRef = useRef(0);
  const seekPreviewVelRef = useRef(0);
  const currentSeekPreviewBoxRef = useRef(null); // 当前活动的气泡窗口引用
  const seekPreviewLyricIdxRef = useRef(-1); // 拖动进度条时：歌词 Hint 防抖（仅 idx 变化才更新）
  const isSeekingRef = useRef(false); // 拖动进度条时置 true，暂停 rAF 自动更新
  const progressTrackRectRef = useRef(null); // 缓存进度条 rect，避免频繁 getBoundingClientRect
  const progressTrackRectTimestampRef = useRef(0); // rect 缓存时间戳
  const seekPointerIdRef = useRef(null);
  const durationRef = useRef(0); // 避免 rAF 闭包读到旧的 duration=0
  // Seek 过程中：记录“拖拽前是否在播放”，并用于 webm seek 卡死恢复的 token
  const seekWasPlayingRef = useRef(false);
  const seekRecoverTokenRef = useRef(0);
  const videoBaselineHeightRef = useRef(null); // PC：记录非全屏时视频可见高度，用于全屏字幕字号等比放大
  const videoSubtitleScaleRef = useRef(1); // PC：视频全屏时字幕字号倍率（缓存，避免每句字幕抖动）
  const videoFullscreenViewportKeyRef = useRef(''); // PC：字幕字号仅随窗口尺寸变化；窗口未变时锁死倍率
  const isLoadingRef = useRef(false); // 标记是否正在加载音频
  const lyricsContainerRef = useRef(null); // 歌词容器引用，用于滚动
  const lyricLineRefs = useRef({}); // 存储每行歌词的引用
  const lastUiTickRef = useRef(0); // 旧逻辑遗留：曾用于 UI 降频（现播放时不再 setCurrentTime）
  const lastTimeLabelSecRef = useRef(-1); // 当前时间文本（秒）缓存，避免每帧 formatTime
  const currentTimeTextRefDesktop = useRef(null);
  const currentTimeTextRefMobile = useRef(null);
  const currentLyricIndexRef = useRef(-1); // 歌词同步使用增量索引，避免每次全量扫描
  const isProgressTrackingRef = useRef(false);
  const lastLyricSyncMsRef = useRef(0); // 歌词同步节流（避免每帧处理）

  const [playing, setPlaying] = useState(false);
  const [lyricsViewportHeight, setLyricsViewportHeight] = useState(0); // 歌词容器可视高度，用于上下留白实现“始终居中”
  const [showFileInfo, setShowFileInfo] = useState(false);
  const [fileInfoPosition, setFileInfoPosition] = useState({ top: 0, left: 0 }); // 文件信息框位置
  const [currentPlayingFormat, setCurrentPlayingFormat] = useState(null); // 当前播放的文件格式信息
  const currentPlayingFormatRef = useRef(null);
  // 同步 ref（避免 hooks 闭包/回调拿到旧 format）
  currentPlayingFormatRef.current = currentPlayingFormat;
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareDialogMeta, setShareDialogMeta] = useState(null);
  const [videoDimensions, setVideoDimensions] = useState({ width: 0, height: 0 }); // 视频原始尺寸
  const [coverDimensions, setCoverDimensions] = useState({ width: 0, height: 0 }); // 封面原始尺寸
  const [isVideoFullscreen, setIsVideoFullscreen] = useState(false); // 视频窗口全屏（不改变底部播放器）
  const [isVideoWindowVisible, setIsVideoWindowVisible] = useState(false); // 仅在 video 实际播放时显示视频窗口
  const [showVideoWindow, setShowVideoWindow] = useState(true); // 手机：全屏按钮改为显示/隐藏视频窗口（播放不停止）
  const desktopControlsRowRef = useRef(null); // 桌面端“打开字幕区那一排”容器
  const volumeSliderWrapRef = useRef(null); // 音量滑条容器（用于测宽）
  const volumeSliderWidthRef = useRef(0); // 记录滑条宽度（隐藏后也能判断何时恢复）
  const [hideVolumeSlider, setHideVolumeSlider] = useState(false); // 挤占时隐藏音量滑条，保证按钮不换行
  // const [progressTrackWidth, setProgressTrackWidth] = useState(null); // 调试：进度条宽度(px)（已禁用）
  const [devViewport, setDevViewport] = useState({ w: 0, h: 0 }); // 开发：窗口尺寸
  const { isLgUp, isMdToLg } = useBreakpoint();

  // 统一判断：是否为“手机模式”（< md）
  const isMobileViewport = !isLgUp && !isMdToLg;
  
  // ========== 初始化音频质量监控器 ==========
  useEffect(() => {
    if (audioContextRef.current && !audioQualityMonitorRef.current) {
      audioQualityMonitorRef.current = new AudioQualityMonitor(audioContextRef.current);
      console.log('[AudioQualityMonitor] 初始化完成');
    }
  }, []);
  
  // ========== 播放新歌时重置监控状态 ==========
  useEffect(() => {
    if (!currentMusic?.id) return;
    console.log('[AudioQualityMonitor] 新歌曲，重置监控状态');
    hasMonitoredCurrentSong.current = false;
    audioQualityMonitorRef.current?.reset();
    setTuacAutoDisabled(false);
  }, [currentMusic?.id]);
  
  useEffect(() => {
    return () => {
      if (fadeOutTimerRef.current) {
        clearInterval(fadeOutTimerRef.current);
        fadeOutTimerRef.current = null;
      }
    };
  }, []);
  

  // 切歌时仅在“用户主动点播”时恢复默认显示
  useEffect(() => {
    if (!currentMusic?.id) return;
    if (userInitiatedChangeRef.current) {
      setShowVideoWindow(true);
      setManualHidden(false);
    }
    userInitiatedChangeRef.current = false;
  }, [currentMusic?.id]);

  // PC：在“非视频全屏”时记录一个基准视频高度，用于全屏时字幕字号等比放大
  useEffect(() => {
    try {
      if (isMobileViewport) {
        videoBaselineHeightRef.current = null;
        return;
      }
      const hasVideo = currentMusic ? !!getVideoFilePath(currentMusic) : false;
      const video = videoRef.current;
      if (!hasVideo || !video) return;
      if (!isVideoFullscreen && isVideoWindowVisible) {
        const h = video.clientHeight;
        if (h && Number.isFinite(h) && h > 0) {
          videoBaselineHeightRef.current = h;
        }
      }
    } catch (_) {}
  }, [currentMusic, isVideoFullscreen, isVideoWindowVisible, isMobileViewport]);

  const ensuredMediaInfoRef = useRef(new Set());
  const ensureVideoMediaInfo = useCallback(async () => {
    await ensureVideoMediaInfoHelper(currentMusic, setContextCurrentMusic, getVideoFilePath, ensuredMediaInfoRef);
  }, [currentMusic, setContextCurrentMusic]);

  // 同步内部状态到 Context（useCallback：避免 hooks 依赖每次变化）
  const updatePlaying = useCallback((value) => {
    setPlaying(value);
    setContextPlaying(value);
  }, [setContextPlaying]);
  
  // 加载保存的设置（来自 playerSettings.js）
  const savedSettings = loadPlayerSettings();
  const tuacDefaults = useTuacDefaults();
  // tuac_core.inf [tuac] 参数 overrides（用于体验页/外部控制），与缺省值合并后下发给引擎
  const [tuacParamOverrides, setTuacParamOverrides] = useState({});
  const mergedTuacParams = useMemo(() => {
    const base = (tuacDefaults && tuacDefaults.tuacParams) ? tuacDefaults.tuacParams : {};
    return { ...base, ...(tuacParamOverrides || {}) };
  }, [tuacDefaults, tuacParamOverrides]);

  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(savedSettings?.volume ?? 1);
  const [muted, setMuted] = useState(savedSettings?.muted ?? false);
  const [playMode, setPlayMode] = useState(savedSettings?.playMode ?? 'sequential'); // sequential, random, repeat
  // 使用 Context 中的 showPlaylist，如果没有则使用内部状态作为兜底
  const [internalShowPlaylist, setInternalShowPlaylist] = useState(false);
  const showPlaylist = contextShowPlaylist !== undefined ? contextShowPlaylist : internalShowPlaylist;
  const setShowPlaylist = contextShowPlaylist !== undefined ? setContextShowPlaylist : setInternalShowPlaylist;
  // max 字幕区（完整歌词列表）开关；mini 字幕区会在播放时自动显示
  const [showLyrics, setShowLyrics] = useState(false);
  // 说明：不再用 isMinimized 来做“停止时收缩”，避免停止/播放切换导致面板布局跳变。
  // 收缩/展开改为对整个面板做 transform（尺寸不变，仅可见区域变化）。
  const [isMinimized] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [lyrics, setLyrics] = useState([]);
  const lyricsRef = useRef([]); // 使用 ref 存储最新歌词，避免闭包问题
  // 播放期间不再用 state 驱动歌词行变化（会导致整组件频繁重渲染，按钮失灵）
  const currentLyricLineRef = useRef(-1);
  const appliedLyricHighlightRef = useRef({ index: -1, theme: null });
  const [loading, setLoading] = useState(false);
  const [isCached, setIsCached] = useState(false); // 当前歌曲是否已缓存
  const wasCachedRef = useRef(false); // 用于跟踪之前的缓存状态，避免重复发送事件
  // 播放速度设置已移除（固定 1.0）
  const [showSettings, setShowSettings] = useState(false); // 显示设置面板
  const [fadeInOut, setFadeInOut] = useState(savedSettings?.fadeInOut ?? true); // 淡入淡出效果（默认开启）
  const [useWasmOpusDecode, setUseWasmOpusDecode] = useState(savedSettings?.useWasmOpusDecode ?? false); // Opus WASM 解码（实验）
  // 稳定播放模式：更大缓冲/更稳（可在设置里手动切换）
  const [stabilityMode, setStabilityMode] = useState(savedSettings?.stabilityMode ?? true);
  const [wasmSimdSupported, setWasmSimdSupported] = useState(null);
  const [surroundEnabled, setSurroundEnabled] = useState(savedSettings?.surroundEnabled ?? tuacDefaults.playerDefaults.surroundEnabled);
  const { user } = useUser();
  const { setSurroundEnabledWrapped } = useSurroundFreeTrial({
    setSurroundEnabled,
    user,
    isAdmin,
    playing,
    currentMusic,
    surroundEnabled,
    t,
    showHintPlus
  });
  const [surroundIntensity, setSurroundIntensity] = useState(savedSettings?.surroundIntensity ?? tuacDefaults.playerDefaults.surroundIntensity);
  const [distortionEnabled, setDistortionEnabled] = useState(savedSettings?.distortionEnabled ?? tuacDefaults.playerDefaults.distortionEnabled);
  const [distortionIntensity, setDistortionIntensity] = useState(savedSettings?.distortionIntensity ?? tuacDefaults.playerDefaults.distortionIntensity);
  const savedDistortionWhenSurroundOnRef = useRef(null); // 3D 关闭时保存的加强谐波状态，打开时恢复
  const handleSetSurroundEnabled = useCallback(
    (next) => {
      if (next === false) {
        savedDistortionWhenSurroundOnRef.current = distortionEnabled;
        setDistortionEnabled(false);
      }
      setSurroundEnabledWrapped(next);
      if (next === true) {
        const saved = savedDistortionWhenSurroundOnRef.current;
        if (saved !== null && saved !== undefined) {
          setDistortionEnabled(saved);
        }
      }
    },
    [distortionEnabled, setSurroundEnabledWrapped]
  );

  // 首次放音“爆音累计检测”（仅本次进入网站第一次播放时执行一次）
  const firstPlayGlitchDetectRef = useRef({
    started: false,
    done: false,
    active: false,
    score: 0,
    reports: 0,
    startMs: 0,
    stopAtMs: 0
  });
  const firstPlayGlitchDetectPrevPlayingRef = useRef(false);

  // TUAC（surround）引擎：对外事件桥接（体验页/外部按钮可控制）
  // - app:tuac-toggle：切换开关
  // - app:tuac-set-enabled：强制设置 { detail: { enabled: boolean } }
  // - app:tuac-enabled-changed：广播当前状态 { detail: { enabled: boolean } }
  useEffect(() => {
    try {
      window.dispatchEvent(new CustomEvent('app:tuac-enabled-changed', { detail: { enabled: !!surroundEnabled } }));
    } catch (_) {}
  }, [surroundEnabled]);

  useEffect(() => {
    const onToggle = () => {
      try {
        handleSetSurroundEnabled(!surroundEnabled);
      } catch (_) {}
    };
    const onSet = (e) => {
      try {
        const enabled = !!(e && e.detail && e.detail.enabled);
        handleSetSurroundEnabled(enabled);
      } catch (_) {}
    };
    window.addEventListener('app:tuac-toggle', onToggle);
    window.addEventListener('app:tuac-set-enabled', onSet);
    return () => {
      window.removeEventListener('app:tuac-toggle', onToggle);
      window.removeEventListener('app:tuac-set-enabled', onSet);
    };
  }, [handleSetSurroundEnabled, surroundEnabled]);

  // TUAC 自动降级提示（当 worklet 不可用时，useAudioGraph 会派发 app:tuac-fallback）
  const tuacFallbackHintAtRef = useRef(0);
  useEffect(() => {
    const onFallback = (e) => {
      try {
        const now = Date.now();
        if (now - (tuacFallbackHintAtRef.current || 0) < 15000) return;
        tuacFallbackHintAtRef.current = now;
        const isSecure = typeof window !== 'undefined' && window.isSecureContext;
        const msg = isSecure
          ? t('player.tuacFallbackHint', '当前设备暂时无法启用虚拟环绕。\n当前设备暂时无法启用空间音效，已自动降级为普通播放。')
          : t('player.tuacFallbackHintInsecure', '虚拟环绕与空间音效需在 HTTPS 或 localhost 下使用。\n当前为普通播放。');
        showUiHint(msg, { duration: 5200 });
      } catch (_) {}
    };
    window.addEventListener('app:tuac-fallback', onFallback);
    return () => window.removeEventListener('app:tuac-fallback', onFallback);
  }, [t]);

  const [bassLevel, setBassLevel] = useState(savedSettings?.bassLevel ?? tuacDefaults.playerDefaults.bassLevel);
  const [reverbLevel, setReverbLevel] = useState(savedSettings?.reverbLevel ?? tuacDefaults.playerDefaults.reverbLevel);
  const [environmentMode, setEnvironmentMode] = useState(savedSettings?.environmentMode ?? tuacDefaults.playerDefaults.environmentMode);
  const [audioEnginePreset, setAudioEnginePreset] = useState(savedSettings?.audioEnginePreset ?? 'transparent');
  const [spaceModeEnabled, setSpaceModeEnabled] = useState(savedSettings?.spaceModeEnabled ?? tuacDefaults.playerDefaults.spaceModeEnabled);
  const [bassEnabled, setBassEnabled] = useState(savedSettings?.bassEnabled ?? tuacDefaults.playerDefaults.bassEnabled);

  // ===== TUAC 参数/GUI：对外事件桥接（体验页/外部 UI 控制） =====
  // 1) tuac_core.inf [tuac] overrides
  useEffect(() => {
    const onSetParams = (e) => {
      try {
        const params = (e && e.detail && (e.detail.params || e.detail)) || null;
        if (!params || typeof params !== 'object') return;
        setTuacParamOverrides((prev) => ({ ...(prev || {}), ...params }));
      } catch (_) {}
    };
    window.addEventListener('app:tuac-set-params', onSetParams);
    return () => window.removeEventListener('app:tuac-set-params', onSetParams);
  }, []);

  // 2) 广播当前 TUAC params（合并后）
  useEffect(() => {
    try {
      window.dispatchEvent(new CustomEvent('app:tuac-params-changed', { detail: { params: mergedTuacParams } }));
    } catch (_) {}
  }, [mergedTuacParams]);

  // 3) tuacgui（播放器侧）设置桥接：体验页可调整这些 state
  useEffect(() => {
    const onSetGui = (e) => {
      try {
        const next = (e && e.detail) || null;
        if (!next || typeof next !== 'object') return;
        if (typeof next.surroundEnabled === 'boolean') handleSetSurroundEnabled(next.surroundEnabled);
        if (typeof next.surroundIntensity === 'number') setSurroundIntensity(next.surroundIntensity);
        if (typeof next.distortionEnabled === 'boolean') setDistortionEnabled(next.distortionEnabled);
        if (typeof next.distortionIntensity === 'number') setDistortionIntensity(next.distortionIntensity);
        if (typeof next.bassEnabled === 'boolean') setBassEnabled(next.bassEnabled);
        if (typeof next.bassLevel === 'number') setBassLevel(next.bassLevel);
        if (typeof next.spaceModeEnabled === 'boolean') setSpaceModeEnabled(next.spaceModeEnabled);
        if (typeof next.reverbLevel === 'number') setReverbLevel(next.reverbLevel);
        if (typeof next.environmentMode === 'string') setEnvironmentMode(next.environmentMode);
      } catch (_) {}
    };
    window.addEventListener('app:tuacgui-set', onSetGui);
    return () => window.removeEventListener('app:tuacgui-set', onSetGui);
  }, [
    handleSetSurroundEnabled,
    setSurroundIntensity,
    setDistortionEnabled,
    setDistortionIntensity,
    setBassEnabled,
    setBassLevel,
    setSpaceModeEnabled,
    setReverbLevel,
    setEnvironmentMode
  ]);

  // 4) 广播当前 tuacgui 状态（体验页用于同步 UI）
  useEffect(() => {
    try {
      window.dispatchEvent(new CustomEvent('app:tuacgui-changed', {
        detail: {
          surroundEnabled: !!surroundEnabled,
          surroundIntensity: Number(surroundIntensity) || 0,
          distortionEnabled: !!distortionEnabled,
          distortionIntensity: Number(distortionIntensity) || 0,
          bassEnabled: !!bassEnabled,
          bassLevel: Number(bassLevel) || 0,
          spaceModeEnabled: !!spaceModeEnabled,
          reverbLevel: Number(reverbLevel) || 0,
          environmentMode: String(environmentMode || 'studio')
        }
      }));
    } catch (_) {}
  }, [
    surroundEnabled,
    surroundIntensity,
    distortionEnabled,
    distortionIntensity,
    bassEnabled,
    bassLevel,
    spaceModeEnabled,
    reverbLevel,
    environmentMode
  ]);
  const [playlistLoop, setPlaylistLoop] = useState(savedSettings?.playlistLoop ?? true); // 播放列表循环播放（默认开启）
  // eslint-disable-next-line no-unused-vars -- setter 保留供持久化/设置面板扩展
  const [abLoopEnabled, setAbLoopEnabled] = useState(savedSettings?.abLoopEnabled ?? false); // A/B 循环开关
  // eslint-disable-next-line no-unused-vars -- setter 保留供持久化/设置面板扩展
  const [abLoopStart, setAbLoopStart] = useState(savedSettings?.abLoopStart ?? null); // A 点（秒）
  // eslint-disable-next-line no-unused-vars -- setter 保留供持久化/设置面板扩展
  const [abLoopEnd, setAbLoopEnd] = useState(savedSettings?.abLoopEnd ?? null); // B 点（秒）
  const abLoopRef = useRef({ enabled: false, start: null, end: null });
  const [visualFadeOutToken, setVisualFadeOutToken] = useState(0);
  const [visualFadeInToken, setVisualFadeInToken] = useState(0);
  const [isVisualSwitching, setIsVisualSwitching] = useState(false);
  const visualFadeMs = fadeInOut
    ? Math.round(Math.max(0, Math.min(1, muted ? 0 : volume)) * FADE_DURATION_MS)
    : 0;
  const hasVideoInCurrentMusic = !!getVideoFilePath(currentMusic);
  const coverUrl = getCoverUrlFromMusicHelper(currentMusic);
  const nextCoverUrl = getCoverUrlFromMusicHelper(getNextMusicForCoverHelper(playlist, currentMusic, playMode, playlistLoop));
  const shouldShowCoverWindow = !hasVideoInCurrentMusic && !!coverUrl;
  const isSingleTrackMode = playMode === 'repeat' || (playlist && playlist.length === 1);
  const isSingleCoverMode = isSingleTrackMode && !hasVideoInCurrentMusic && !!coverUrl;

  const {
    coverLayerAUrl,
    coverLayerBUrl,
    coverLayerAType,
    coverLayerBType,
    coverLayerAOpacity,
    coverLayerBOpacity,
    coverLayerANoFadeIn,
    coverLayerBNoFadeIn,
    setCoverLayerAOpacity,
    setCoverLayerBOpacity,
    coverFadeOutStartedRef,
    coverActiveLayerRef
  } = useCoverLayers({
    currentMusic,
    hasVideoInCurrentMusic,
    coverUrl,
    nextCoverUrl,
    fadeInOut,
    isSingleCoverMode,
    visualFadeOutToken,
    visualFadeInToken
  });

  useEffect(() => {
    if (!currentMusic?.id) return;
    if (!fadeInOut) {
      setIsVisualSwitching(false);
      return;
    }
    if (!hasVideoInCurrentMusic && coverUrl) {
      setIsVisualSwitching(true);
      return;
    }
    setIsVisualSwitching(false);
  }, [currentMusic?.id, fadeInOut, hasVideoInCurrentMusic, coverUrl]);

  // 读取封面尺寸，用于封面窗口保持完整显示
  useEffect(() => {
    if (!coverUrl) {
      setCoverDimensions({ width: 0, height: 0 });
      return;
    }
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth || img.width || 0;
      const h = img.naturalHeight || img.height || 0;
      setCoverDimensions({ width: w, height: h });
    };
    img.onerror = () => {
      setCoverDimensions({ width: 0, height: 0 });
    };
    img.src = coverUrl;
  }, [coverUrl]);
  
  // 获取当前界面语言，用于设置默认翻译目标
  const { i18n: i18nForInit } = useTranslation();
  const currentLangForInit = i18nForInit?.language || 'ja';
  
  const [lyricSettings, setLyricSettings] = useState({
    fontSize: savedSettings?.lyricSettings?.fontSize ?? 16,
    position: savedSettings?.lyricSettings?.position ?? 'center', // top, center, bottom
    showTranslation: savedSettings?.lyricSettings?.showTranslation ?? true, // 默认开启翻译字幕功能
    translationMap: getDefaultTranslationMap(savedSettings, currentLangForInit),
    highlightTheme: savedSettings?.lyricSettings?.highlightTheme ?? 'purple', // amber | cyan | purple | green
    highlightGlow: savedSettings?.lyricSettings?.highlightGlow ?? 'medium' // low | medium | high
  });
  // eslint-disable-next-line no-unused-vars
  const [equalizerPreset, setEqualizerPreset] = useState(savedSettings?.equalizerPreset ?? 'normal'); // 均衡器预设
  const [audioContext, setAudioContext] = useState(null); // Web Audio API上下文
  const [gainNodes, setGainNodes] = useState(null); // 增益节点数组
  // eslint-disable-next-line no-unused-vars
  const [lyricOffset, setLyricOffset] = useState(savedSettings?.lyricOffset ?? 0); // 歌词时间偏移（秒）
  
  const [audioFocusMode, setAudioFocusMode] = useState(savedSettings?.audioFocusMode ?? 'exclusive'); // 音频焦点模式：'exclusive' 独占（默认） | 'shared' 共享

  const { isMobileDevice } = useAudioOutput();

  useEffect(() => {
    abLoopRef.current = { enabled: abLoopEnabled, start: abLoopStart, end: abLoopEnd };
  }, [abLoopEnabled, abLoopStart, abLoopEnd]);

  // 停止播放时：关闭展开面板（避免残留占用空间/遮挡）
  useEffect(() => {
    if (playing) return;
    try { setShowPlaylist(false); } catch (_) {}
    try { setShowLyrics(false); } catch (_) {}
    try { setShowSettings(false); } catch (_) {}
  }, [playing, setShowPlaylist, setShowLyrics, setShowSettings]);

  // 自动隐藏逻辑：
  // - 手动暂停播放歌曲和歌曲播放完毕后，如果播放器不是Hover状态，启动自动隐藏倒计时（10秒）
  // - 开始播放歌曲，停止自动隐藏倒计时
  // - 播放器区域内交互会重置倒计时；鼠标在播放器内部时暂停倒计时
  const [isPlayerHovering, setIsPlayerHovering] = useState(false);
  const { autoHidden, bump: bumpAutoHideCountdown, setAutoHidden } = useAutoHideOnStop({
    playing,
    isFullscreen,
    // 鼠标在播放器内部：暂停倒计时；鼠标离开：开始计时
    suspend: isPlayerHovering,
    delayMs: AUTO_HIDE_DELAY_MS
  });
  const [manualHidden, setManualHidden] = useState(false);

  useEffect(() => {
    // 进入全屏时，恢复显示播放器面板
    if (isFullscreen) setManualHidden(false);
  }, [isFullscreen]);

  const handlePlaylistChange = useCallback((music, index) => {
    userInitiatedChangeRef.current = true;
    if (onPlaylistChange) onPlaylistChange(music, index);
  }, [onPlaylistChange]);

  // 视频窗口是否应该显示：视频在播 or 无视频时播放中 + 有封面
  const activeMediaElement = getActiveMediaElement(hasVideoInCurrentMusic, videoRef, audioRef);
  const isStopped = !!currentMusic && !playing && !!activeMediaElement?.ended;
  const shouldShowVideoWindow = !autoHidden && !manualHidden && showVideoWindow && !isStopped && (
    hasVideoInCurrentMusic
    || isVideoWindowVisible
    || shouldShowCoverWindow
    || isVisualSwitching
  );

  // 将播放器实际高度写入 CSS 变量，供视频浮窗对齐/页面留白使用
  usePlayerHeightCssVar({ playerRef, isFullscreen });

  const { videoWindowStyle, videoDockStyle, dockingReady } = useVideoDocking({
    currentMusic,
    isFullscreen,
    shouldShowVideoWindow,
    isVideoFullscreen,
    playerRef,
    getVideoFilePath,
    videoDimensions,
    coverDimensions,
    showCover: shouldShowCoverWindow
  });

  useVideoWindowVisibleSync({
    currentMusic,
    videoRef,
    getVideoFilePath,
    setIsVideoWindowVisible
  });

  const hasVideo = currentMusic ? !!getVideoFilePath(currentMusic) : false;
  const { sleepTimer, sleepTimerMinutes, setSleepTimerHandler } = useSleepTimer({
    initialSleepTimerMinutes: savedSettings?.sleepTimerMinutes ?? 0,
    hasVideo,
    getActiveMediaElement,
    videoRef,
    audioRef,
    updatePlaying,
    fadeInOut,
    t
  });

  // 当设置改变时保存到 localStorage（由 playerSettings 统一构建；依赖与 BUILD_INPUT_KEYS 一致）
  useEffect(() => {
    savePlayerSettings(buildSettingsFromState({
      volume,
      muted,
      playMode,
      sleepTimerMinutes,
      fadeInOut,
      useWasmOpusDecode,
      stabilityMode,
      surroundEnabled,
      surroundIntensity,
      distortionEnabled,
      distortionIntensity,
      bassLevel,
      reverbLevel,
      environmentMode,
      audioEnginePreset,
      spaceModeEnabled,
      bassEnabled,
      lyricSettings,
      equalizerPreset,
      lyricOffset,
      playlistLoop,
      abLoopEnabled,
      abLoopStart,
      abLoopEnd,
      audioFocusMode
    }));
  }, [volume, muted, playMode, sleepTimerMinutes, fadeInOut, useWasmOpusDecode, stabilityMode, surroundEnabled, surroundIntensity, distortionEnabled, distortionIntensity, bassLevel, reverbLevel, environmentMode, audioEnginePreset, spaceModeEnabled, bassEnabled, lyricSettings, equalizerPreset, lyricOffset, playlistLoop, abLoopEnabled, abLoopStart, abLoopEnd, audioFocusMode]);

  useEffect(() => {
    let active = true;
    detectWasmSimdSupport().then((supported) => {
      if (!active) return;
      setWasmSimdSupported(supported);
      if (!supported) {
        setUseWasmOpusDecode(false);
      }
    }).catch(() => {
      if (!active) return;
      setWasmSimdSupported(false);
      setUseWasmOpusDecode(false);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (wasmAudioUrlRef.current) {
        URL.revokeObjectURL(wasmAudioUrlRef.current);
        wasmAudioUrlRef.current = null;
      }
    };
  }, []);
  
  // 应用保存的音量设置（muted/volume 变化时同步到媒体元素）
  useEffect(() => {
    const hasVideo = currentMusic ? !!getVideoFilePath(currentMusic) : false;
    const mediaEl = getActiveMediaElement(hasVideo, videoRef, audioRef);
    if (!mediaEl) return;
    mediaEl.volume = muted ? 0 : volume;
    // 兜底：避免切换 hasVideo 时另一个元素残留音量
    if (audioRef.current && audioRef.current !== mediaEl) audioRef.current.volume = muted ? 0 : volume;
    if (videoRef.current && videoRef.current !== mediaEl) videoRef.current.volume = muted ? 0 : volume;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [muted, volume, currentMusic?.id]);

  // 站内 UI 语言码：ja/en/cn/tw（注意：歌词翻译接口仍使用外部中文地区码）
  const currentLang = i18n.language || 'cn';

  const resolveTargetTranslationLang = React.useCallback((music) => {
    return resolveTargetTranslationLangHelper(music, lyricSettingsRef.current?.translationMap || {}, currentLang);
  }, [currentLang]);

  // mini 字幕：播放时展示（只显示当前高亮一句）
  const hasLyrics = !!currentMusic?.lyrics_type && lyrics.length > 0;

  // 取消面板歌词：不显示 mini；字幕统一叠加在视频/封面上
  const shouldShowMiniLyrics = false;
  const shouldShowVideoSubtitles = !isMinimized && playing && hasLyrics && (hasVideoInCurrentMusic || shouldShowCoverWindow);

  const highlightTheme = getHighlightThemeClasses(lyricSettings.highlightTheme);
  const highlightGlowAlpha = getGlowAlpha(lyricSettings.highlightGlow);
  const highlightThemeRef = useRef(highlightTheme);
  const highlightGlowAlphaRef = useRef(highlightGlowAlpha);
  const lyricSettingsRef = useRef(lyricSettings);
  const showLyricsRef = useRef(showLyrics);
  const lyricOffsetRef = useRef(lyricOffset);
  // 关键：歌词更新链路大量使用 ref/rAF，避免闭包读到旧的全屏/视口状态导致“下一句字幕变小”
  const isVideoFullscreenRef = useRef(isVideoFullscreen);
  const isMobileViewportRef = useRef(isMobileViewport);
  const miniLyricTextRef = useRef(null);
  const miniLyricTransTextRef = useRef(null);
  const lyricSyncRefsRef = useRef({});
  const loadMusicRefsRef = useRef({});
  const loadMusicSettersRef = useRef({});
  const loadMusicCallbacksRef = useRef({});
  const progressTrackingRefsRef = useRef({});
  const progressTrackingCallbacksRef = useRef({});
  const { startProgressTracking, stopProgressTracking, handleSeekPointerDown, handleSeekPointerMove, handleSeekPointerUpOrCancel, updateAllProgressVisual } = useProgressTracking({
    refsRef: progressTrackingRefsRef,
    callbacksRef: progressTrackingCallbacksRef
  });
  const { loadMusic, checkCacheStatus } = useLoadMusic({
    refsRef: loadMusicRefsRef,
    settersRef: loadMusicSettersRef,
    callbacksRef: loadMusicCallbacksRef
  });

  useCacheStatusOnMusic({
    currentMusic,
    playing,
    checkCacheStatus,
    setIsCached,
    wasCachedRef
  });

  useEffect(() => {
    highlightThemeRef.current = highlightTheme;
    highlightGlowAlphaRef.current = highlightGlowAlpha;
  }, [highlightTheme, highlightGlowAlpha]);
  useEffect(() => {
    lyricSettingsRef.current = lyricSettings;
  }, [lyricSettings]);
  useEffect(() => {
    showLyricsRef.current = showLyrics;
  }, [showLyrics]);
  useEffect(() => {
    lyricOffsetRef.current = lyricOffset;
  }, [lyricOffset]);
  useEffect(() => {
    isVideoFullscreenRef.current = isVideoFullscreen;
  }, [isVideoFullscreen]);
  useEffect(() => {
    isMobileViewportRef.current = isMobileViewport;
  }, [isMobileViewport]);

  useVideoSubtitleScale({
    isVideoFullscreen,
    isMobileViewport,
    currentMusicId: currentMusic?.id,
    videoRef,
    lyricSettingsRef,
    miniLyricTextRef,
    miniLyricTransTextRef,
    videoSubtitleScaleRef,
    videoFullscreenViewportKeyRef,
    isVideoFullscreenRef,
    isMobileViewportRef
  });

  lyricSyncRefsRef.current = {
    lyricsContainerRef,
    lyricLineRefs,
    appliedLyricHighlightRef,
    currentLyricLineRef,
    lyricSettingsRef,
    lyricsRef,
    miniLyricTextRef,
    miniLyricTransTextRef,
    highlightThemeRef,
    highlightGlowAlphaRef,
    showLyricsRef,
    isVideoFullscreenRef,
    isMobileViewportRef,
    videoSubtitleScaleRef
  };
  const { applyLyricHighlight, clearLyricHighlight, updateMiniLyric } = useLyricsSync({ refsRef: lyricSyncRefsRef, shouldShowVideoSubtitles });

  // mini 字幕显示/主题/翻译设置变化时，刷新一次内容与样式（不靠 state 触发逐帧渲染）
  useEffect(() => {
    // 视频模式字幕叠加在视频上，也需要刷新
    if (!(shouldShowMiniLyrics || shouldShowVideoSubtitles)) return;
    updateMiniLyric(currentLyricLineRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowMiniLyrics, shouldShowVideoSubtitles, lyrics.length, lyricSettings.showTranslation, lyricSettings.highlightTheme, lyricSettings.highlightGlow]);

  // 仅本次进入网站第一次放音：在开始播放后的短窗口内累计 Worklet 上报的 “GLITCH”
  useEffect(() => {
    const state = firstPlayGlitchDetectRef.current;
    if (state.done) return;

    // 会话级开关（刷新页面才会重置；SPA 内切换路由仍视为同一次“进入网站”）
    const sessionKey = 'tenryu:first-play-glitch-detect:done';
    try {
      if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(sessionKey) === '1') {
        state.done = true;
        return;
      }
    } catch (_) {}

    const wasPlaying = !!firstPlayGlitchDetectPrevPlayingRef.current;
    firstPlayGlitchDetectPrevPlayingRef.current = !!playing;

    // 只在“第一次从未播放->开始播放”触发检测窗口
    if (!state.started && !wasPlaying && playing) {
      const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
      state.started = true;
      state.active = true;
      state.score = 0;
      state.reports = 0;
      state.startMs = now;
      // 检测窗口：只覆盖“开始放音后的前一小段”
      state.stopAtMs = now + 22000; // 22s
    }

    if (!state.active) return;

    const stopDetect = () => {
      state.active = false;
      state.done = true;
      try {
        if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(sessionKey, '1');
      } catch (_) {}
    };

    const onGlitch = (e) => {
      try {
        const s = firstPlayGlitchDetectRef.current;
        if (!s.active || s.done) return;
        const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
        if (now > (s.stopAtMs || 0)) {
          stopDetect();
          return;
        }
        const d = e?.detail || {};
        const points = Number(d.points);
        const clicks = Number(d.clicks);
        const rms = Number(d.rms);

        // 可靠性策略（轻量 & 抗误报）：
        // - Worklet 只有在 points>=6 时才上报，已经是“持续异常”
        // - 主线程再做一次加权累计：把 “更像爆音” 的情况权重提高
        const p = Number.isFinite(points) ? points : 0;
        const c = Number.isFinite(clicks) ? clicks : 0;
        const r = Number.isFinite(rms) ? rms : 0;

        let add = 1; // 基础：一次上报=一次异常
        if (p >= 10) add += 1;
        if (c >= 10) add += 1;
        // 在正常响度下出现 glitch 更值得关注；接近静音时降权
        if (r < 0.03) add = Math.max(1, add - 1);

        s.reports += 1;
        s.score += add;

        // 触发阈值：宁可少报也不要骚扰用户
        const hit = (s.score >= 4) || (s.reports >= 3);
        if (hit) {
          // 浮动 Hint 警告，并停止检测
          try {
            showUiHint(
              t('player.glitchDetectHint') || '检测到设备当前存在较明显的爆音/卡顿风险，已自动切换为更稳定的播放策略。建议打开「稳定播放（防卡顿）」或关闭空间模式。',
              { duration: 6000 }
            );
          } catch (_) {}
          stopDetect();
        }
      } catch (_) {}
    };

    window.addEventListener('app:tuac-glitch', onGlitch);
    const timerId = setInterval(() => {
      try {
        const s = firstPlayGlitchDetectRef.current;
        if (!s.active || s.done) return;
        const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
        if (now > (s.stopAtMs || 0)) stopDetect();
      } catch (_) {}
    }, 800);

    return () => {
      window.removeEventListener('app:tuac-glitch', onGlitch);
      clearInterval(timerId);
    };
  }, [playing, t]);

  // 统一控件样式：让按钮/图标/滑块更精致一致
  const cx = (...classes) => classes.filter(Boolean).join(' ');

  // 按钮点击时显示浮动 hint 的辅助函数
  const handleButtonClickWithHint = (e, originalHandler, hintText) => {
    if (originalHandler) {
      originalHandler(e);
    }
    if (hintText && e.currentTarget) {
      showUiHint(hintText, { anchorEl: e.currentTarget, duration: 1200 });
    }
  };

  // eslint-disable-next-line no-unused-vars
  const smallIconButtonBase = 'h-8 w-8 inline-flex items-center justify-center rounded-lg transition-all duration-150 select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/70 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900 active:scale-[0.98]';
  // 播放列表展开时：点击播放器区域外收起（避免页面切换/误触影响）
  useEffect(() => {
    if (!showPlaylist) return;
    const onDocMouseDown = (e) => {
      const playerEl = playerRef.current;
      if (!playerEl) return;
      // 如果点击的是【+】按钮（添加到播放列表按钮），不关闭播放列表
      if (e.target.closest('[data-add-to-playlist-btn="true"]')) {
        return;
      }
      if (!playerEl.contains(e.target)) {
        setShowPlaylist(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [showPlaylist, setShowPlaylist]);

  // 点击播放器空白区域：展开/收起播放列表
  const handlePlayerBlankClick = (e) => {
    const target = e.target;

    // 如果文件信息框显示，点击播放器任何地方都关闭它
    if (showFileInfo) {
      const button = target.closest('button');
      // 如果点击的不是信息按钮，关闭信息框
      if (!button || (!button.textContent?.includes(t('player.unknownFormat')) && !button.textContent?.includes('MB') && !button.textContent?.includes('KB') && !button.textContent?.includes('GB'))) {
        setShowFileInfo(false);
      }
    }
    if (target && typeof target.closest === 'function') {
      // 点击控件不触发展开/收起（避免与按钮/滑块等冲突）
      if (target.closest('button, a, input, select, textarea, [data-no-toggle]')) return;
      // 点击播放列表区域本身不触发展开/收起
      if (playlistPanelRef.current && playlistPanelRef.current.contains(target)) return;
      // 点击设置面板区域本身不触发展开/收起
      if (settingsPanelRef.current && settingsPanelRef.current.contains(target)) return;
    }

    // 只有点到真正空白处才关闭设置面板（避免“按钮要点多次”）
    if (showSettings) {
      setShowSettings(false);
      return;
    }
    setShowPlaylist(prev => !prev);
  };

  usePlayerGestures({
    playerRef,
    settingsPanelRef,
    settingsButtonRef,
    showSettings,
    setShowSettings,
    showLyrics,
    showPlaylist,
    isFullscreen,
    currentMusicId: currentMusic?.id
  });

  const getCurrentLanguageTitle = (music) => getCurrentLanguageTitleHelper(music, currentLang);

  const { applyAudioFocusMode } = useMediaSession({
    currentMusic,
    playing,
    onNext,
    onPrevious,
    getCurrentLanguageTitle,
    getVideoFilePath,
    videoRef,
    audioRef,
    audioFocusMode,
    isMobileDevice
  });

  const { resumeAudioContext, rebuildAudioGraph, applyEffectParams, applyEnvironmentMode, resetAudioContext } = useAudioGraph({
    audioContextRef,
    mediaSourceRefs,
    tuacEngineRef,
    analyserRef,
    bassShelfRef,
    dryGainRef,
    wetGainRef,
    mergeRef,
    activeMediaTypeRef,
    fidelityRef,
    audioRef,
    videoRef,
    gainNodesState: [gainNodes, setGainNodes],
    audioContext,
    setAudioContext,
    equalizerPreset,
    surroundEnabled,
    surroundIntensity,
    distortionEnabled,
    distortionIntensity,
    bassLevel,
    reverbLevel,
    environmentMode,
    spaceModeEnabled,
    bassEnabled,
    stabilityMode,
    tuacParams: mergedTuacParams,
    // 音频质量监控相关参数
    audioQualityMonitorRef,
    hasMonitoredCurrentSong,
    setTuacAutoDisabled,
    tuacAutoDisabled
  });

  // 稳定播放模式切换：需要关闭并重建 AudioContext（latencyHint 只在构造时生效）
  const stabilityModeAppliedRef = useRef(stabilityMode);
  useEffect(() => {
    if (!resetAudioContext || !resumeAudioContext || !rebuildAudioGraph) return;
    if (stabilityModeAppliedRef.current === stabilityMode) return;
    stabilityModeAppliedRef.current = stabilityMode;

    let cancelled = false;
    (async () => {
      const hasVideo = currentMusic ? !!getVideoFilePath(currentMusic) : false;
      const mediaEl = getActiveMediaElement(hasVideo, videoRef, audioRef);
      const wasPlaying = !!playing;
      const keepTime = mediaEl?.currentTime ?? 0;

      try { if (mediaEl && !mediaEl.paused) mediaEl.pause(); } catch (_) {}
      try { await resetAudioContext(); } catch (_) {}
      if (cancelled) return;
      try { await resumeAudioContext(); } catch (_) {}
      if (cancelled) return;
      try { await rebuildAudioGraph(hasVideo ? 'video' : 'audio'); } catch (_) {}
      if (cancelled) return;

      try {
        if (mediaEl && Number.isFinite(keepTime) && keepTime > 0) {
          mediaEl.currentTime = keepTime;
        }
      } catch (_) {}

      if (wasPlaying && mediaEl) {
        try {
          await mediaEl.play();
        } catch (_) {}
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [stabilityMode, resetAudioContext, resumeAudioContext, rebuildAudioGraph, currentMusic, videoRef, audioRef, playing]);

  // 频谱分析节点就绪后通知体验页（节点本身不序列化，放到 window 上供读取）
  useEffect(() => {
    try {
      if (analyserRef.current) {
        window.__TENRYU_AUDIO_ANALYSER__ = analyserRef.current;
        window.dispatchEvent(new CustomEvent('app:audio-analyser-ready'));
      }
    } catch (_) {}
  }, [audioContext, surroundEnabled, equalizerPreset]);

  // 加载音乐
  useEffect(() => {
    if (!currentMusic || !audioRef.current) return;
    const musicId = currentMusic.id ?? null;
    const videoLayerReady = coverLayerAType === 'video' || coverLayerBType === 'video';
    if (hasVideoInCurrentMusic && !videoLayerReady) {
      pendingVideoLoadRef.current = musicId;
      return;
    }
    if (lastLoadedMusicIdRef.current === musicId && pendingVideoLoadRef.current !== musicId) {
      return;
    }
    pendingVideoLoadRef.current = null;
    lastLoadedMusicIdRef.current = musicId;
    // 只在“切歌/首次载入”时调用 loadMusic
    // 注意：不要把 externalPlaying 放进依赖里，否则“暂停→播放”会触发 loadMusic 重载音频并重置到 0
    loadMusic(currentMusic, externalPlaying === true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMusic, hasVideoInCurrentMusic, coverLayerAType, coverLayerBType]);
  
  // 当 duration 确定后，更新码流（从实际文件计算）
  useEffect(() => {
    if (duration > 0 && currentPlayingFormat && currentPlayingFormat.file_size) {
      const calculatedBitrate = Math.round((currentPlayingFormat.file_size * 8) / duration);
      // 只有当计算出的码流与当前不同时才更新（避免无限循环）
      if (!currentPlayingFormat.bitrate || Math.abs(currentPlayingFormat.bitrate - calculatedBitrate) > 1000) {
        setCurrentPlayingFormat(prev => ({
          ...prev,
          bitrate: calculatedBitrate
        }));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration, currentPlayingFormat?.file_size]);

  // 当音效结构改变时（开关/均衡）重建整张图
  useEffect(() => {
    if (!audioContext) return;
    rebuildAudioGraph().catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equalizerPreset, surroundEnabled, audioContext]);

  // 环境/空间切换：优先只更新 IR，失败则重建图
  useEffect(() => {
    if (!audioContext || !surroundEnabled) return;
    applyEnvironmentMode().then((ok) => {
      if (!ok) rebuildAudioGraph().catch(() => {});
    });
  }, [audioContext, surroundEnabled, environmentMode, applyEnvironmentMode, rebuildAudioGraph]);

  // 当仅效果参数改变时（环绕强度/虚拟低音/混响/谐波）只更新参数，不断开连接，使调节即时生效
  useEffect(() => {
    if (!audioContext || !surroundEnabled) return;
    const applied = applyEffectParams();
    if (!applied) {
      rebuildAudioGraph().catch(() => {});
    }
  }, [audioContext, surroundEnabled, surroundIntensity, bassLevel, reverbLevel, distortionEnabled, distortionIntensity, spaceModeEnabled, bassEnabled, applyEffectParams, rebuildAudioGraph]);

  // 当翻译设置改变时，重新加载歌词
  useEffect(() => {
    if (currentMusic && currentMusic.lyrics_type) {
      loadLyrics(null, currentMusic.lyrics_type, currentMusic);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lyricSettings.showTranslation, lyricSettings.translationMap, currentMusic, currentLang]);

  // 使用工具函数生成流式传输URL（隐藏实际文件路径）
  const getStreamUrl = useCallback((music, format = 'auto') => {
    return getMusicStreamUrl(music, format);
  }, []);

  // 播放卡死/自动停止恢复（华为浏览器/微信内置浏览器等平台偶发）
  const playbackStallStateRef = useRef({
    lastTime: 0,
    lastAdvanceMs: 0,
    lastRecoverMs: 0,
    token: 0
  });

  const recoverStuckPlayback = useCallback(async (reason) => {
    const music = currentMusic;
    if (!music) return false;
    const now = Date.now();
    const state = playbackStallStateRef.current;
    // 避免频繁重试导致更卡
    if (now - (state.lastRecoverMs || 0) < 6000) return false;
    state.lastRecoverMs = now;
    const myToken = ++state.token;

    const hasVideo = !!getVideoFilePath(music);
    const mediaEl = getActiveMediaElement(hasVideo, videoRef, audioRef);
    if (!mediaEl) return false;

    const keepTime = mediaEl.currentTime ?? 0;

    // 先尝试轻量恢复：resume + play
    try { await resumeAudioContext(); } catch (_) {}
    try {
      await mediaEl.play();
      return true;
    } catch (_) {}

    // 再尝试重载媒体资源（不切歌）：重新设置 src + load + 重新连图
    try { mediaEl.pause(); } catch (_) {}
    try {
      const baseUrl = getStreamUrl(music, 'webm');
      const url = baseUrl
        ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}recover=${Date.now()}`
        : (mediaEl.currentSrc || mediaEl.src || '');
      if (url) {
        mediaEl.src = url;
        mediaEl.load();
      }
    } catch (_) {}

    // 等一等元数据（避免直接 seek 报错）
    try {
      await new Promise((resolve) => {
        let done = false;
        const onOk = () => {
          if (done) return;
          done = true;
          cleanup();
          resolve(true);
        };
        const onTimeout = () => {
          if (done) return;
          done = true;
          cleanup();
          resolve(false);
        };
        const cleanup = () => {
          try { mediaEl.removeEventListener('loadedmetadata', onOk); } catch (_) {}
          try { mediaEl.removeEventListener('canplay', onOk); } catch (_) {}
          try { clearTimeout(tid); } catch (_) {}
        };
        const tid = setTimeout(onTimeout, 1200);
        try { mediaEl.addEventListener('loadedmetadata', onOk); } catch (_) {}
        try { mediaEl.addEventListener('canplay', onOk); } catch (_) {}
      });
    } catch (_) {}

    if (myToken !== playbackStallStateRef.current.token) return false;

    try {
      const dur = mediaEl.duration;
      if (Number.isFinite(keepTime) && keepTime > 0) {
        const safeT = (Number.isFinite(dur) && dur > 0) ? Math.min(Math.max(0, keepTime), Math.max(0, dur - 0.2)) : keepTime;
        mediaEl.currentTime = safeT;
      }
    } catch (_) {}

    try { await rebuildAudioGraph(hasVideo ? 'video' : 'audio'); } catch (_) {}
    if (myToken !== playbackStallStateRef.current.token) return false;

    try {
      await mediaEl.play();
      return true;
    } catch (err) {
      try {
        console.warn('[Player] recover failed:', reason, err);
      } catch (_) {}
      return false;
    }
  }, [currentMusic, getStreamUrl, videoRef, audioRef, resumeAudioContext, rebuildAudioGraph]);

  useWakeLock({
    playing,
    wakeLockRef,
    lockInterruptionRef,
    videoRef,
    audioRef,
    activeMediaTypeRef,
    showHintPlus,
    lockPlaybackInterruptedT: t('player.lockPlaybackInterrupted')
  });

  // max 字幕区打开/样式变化时：重应用当前高亮，并确保居中（不依赖 state 驱动）
  useEffect(() => {
    if (!showLyrics) return;
    requestAnimationFrame(() => {
      const idx = currentLyricLineRef.current;
      if (idx >= 0) {
        applyLyricHighlight(idx, { force: true, ensureCenter: true });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLyrics, lyricsViewportHeight, lyricSettings.showTranslation, lyricSettings.fontSize, lyricSettings.highlightTheme, lyricSettings.highlightGlow, lyrics.length]);

  useLyricsViewportHeight({
    lyricsContainerRef,
    showLyrics,
    setLyricsViewportHeight
  });

  useDesktopControlsOverflow({
    desktopControlsRowRef,
    volumeSliderWrapRef,
    volumeSliderWidthRef,
    isLgUp,
    hideVolumeSlider,
    setHideVolumeSlider,
    deps: [currentMusic?.id, showLyrics, showPlaylist, playMode, playlist?.length]
  });

  // 进入 lg+ 时重置“挤占隐藏”状态，让测量逻辑重新接管
  useEffect(() => {
    if (isLgUp) setHideVolumeSlider(false);
  }, [isLgUp]);

  // 切歌时重置“是否前进”计时，避免误触发恢复
  useEffect(() => {
    const state = playbackStallStateRef.current;
    state.lastTime = 0;
    state.lastAdvanceMs = Date.now();
  }, [currentMusic?.id, hasVideoInCurrentMusic]);

  // 开发模式：右下角显示窗口尺寸（避免 ResizeObserver loop，用 rAF 节流）
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    let rafId = 0;
    let lastW = -1;
    let lastH = -1;

    const updateNow = () => {
      const w = window.innerWidth || 0;
      const h = window.innerHeight || 0;
      if (w === lastW && h === lastH) return;
      lastW = w;
      lastH = h;
      setDevViewport({ w, h });
    };

    const schedule = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        updateNow();
      });
    };

    schedule();
    window.addEventListener('resize', schedule);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', schedule);
    };
  }, []);

  // 使用工具函数生成歌词文件的流式传输URL
  const getLyricsStreamUrlLocal = useCallback((music) => {
    return getLyricsStreamUrl(music);
  }, []);

  // 使用工具函数生成封面图片的流式传输URL
  const getCoverStreamUrlLocal = useCallback((music) => {
    return getCoverStreamUrl(music);
  }, []);

  // 加载歌词
  const loadLyrics = async (lyricsFile, lyricsType, music = null) => {
    try {
      const musicToUse = music || currentMusic;
      const streamUrl = musicToUse ? getLyricsStreamUrlLocal(musicToUse) : null;
      const urlToUse = streamUrl || lyricsFile;
      if (!urlToUse) return;

      const lyricsFileName = (lyricsFile || urlToUse).split('/').pop() || 'N/A';
      console.log('Loading lyrics file:', lyricsFileName, 'Type:', lyricsType);

      await loadLyricsWithTranslation({
        lyricsFile,
        lyricsType,
        music: musicToUse,
        urlToUse,
        axios,
        getLyricsStreamUrl: getLyricsStreamUrlLocal,
        lyricSettings,
        resolveTargetTranslationLang,
        setLyrics,
        lyricsRef,
        clearLyricHighlight,
        updateMiniLyric,
        currentLyricIndexRef,
        currentMusicId: currentMusic?.id
      });
      console.log('Lyrics loaded and set, count:', lyricsRef.current?.length ?? 0);
    } catch (error) {
      console.error('Load lyrics error:', error);
      setLyrics([]);
      lyricsRef.current = [];
      clearLyricHighlight();
      updateMiniLyric(-1);
      currentLyricIndexRef.current = -1;
    }
  };

  // 播放/暂停
  const togglePlay = async () => {
    if (!currentMusic) return;

    try {
      const activeType = activeMediaTypeRef.current || (hasVideoInCurrentMusic ? 'video' : 'audio');
      const activeElement = activeType === 'video' ? videoRef.current : audioRef.current;
      if (playing) {
        // 暂停
        if (activeElement) {
          activeElement.pause();
        }
        if (iosShadowAudioRef.current && audioRef.current) {
          try { audioRef.current.pause(); } catch (_) {}
          iosShadowAudioRef.current = false;
        }
        updatePlaying(false);
        stopProgressTracking();
        stopPlayHistoryTracking();
      } else {
        // 播放
        const mediaElement = activeElement || audioRef.current || videoRef.current;
        if (muted) {
          const newVolume = volume || 0.5;
          setMuted(false);
          if (volume === 0) setVolume(newVolume);
          if (audioRef.current) audioRef.current.volume = newVolume;
          if (videoRef.current) videoRef.current.volume = newVolume;
        }
        if (mediaElement) {
          await resumeAudioContext();
          await mediaElement.play();
        }
        await setupIosShadowAudio(audioRef, videoRef, getStreamUrl, currentMusic, iosShadowAudioRef, hasVideoInCurrentMusic);
        updatePlaying(true);
        startProgressTracking();
        startPlayHistoryTracking();
        recordPlay();
        
        // 开发模式：开始播放时输出 SIMD 指令集使用情况到控制台
        logSimdOnPlay(audioContextRef.current);
        if (process.env.NODE_ENV === 'development') {
          mpLog('[MusicPlayer] WASM 配置', {
            useWasmOpusDecode,
            wasmSimdSupported: wasmSimdSupported === null ? '检测中' : wasmSimdSupported
          });
        }
        
        // 播放时立即应用独占模式，尽量独占声卡（音乐播放程序应该独占）
        if ('mediaSession' in navigator) {
          applyAudioFocusMode(true);
        }
        
        // 如果启用淡入效果（音频/视频都支持）
        if (fadeInOut) {
          const mediaElement = getActiveMediaElement(hasVideo, videoRef, audioRef);
          if (mediaElement && mediaElement.volume === 0) {
            setIsVisualSwitching(false);
            setVisualFadeInToken((v) => v + 1);
            const targetVolume = muted ? 0 : volume;
            runFadeIn(mediaElement, targetVolume);
          }
        }
      }
    } catch (error) {
      console.error('Play error:', error);
    }
  };

  // eslint: togglePlay 每次 render 都会变；用 ref 供事件桥接稳定调用
  const togglePlayRef = useRef(null);
  // 直接在 render 中同步 ref（避免 useEffect 依赖 togglePlay 引发 eslint warning）
  togglePlayRef.current = togglePlay;

  // （体验页播放控制桥接）事件监听放到 start/stopPlayHistoryTracking 定义之后，避免 no-use-before-define；
  // 并使用 ref 读取最新状态，避免 exhaustive-deps 警告。

  useEffect(() => {
    const timer = setInterval(() => {
      try {
        const mediaElement = getActiveMediaElement(hasVideo, videoRef, audioRef);
        const dur = (mediaElement && (mediaElement.duration || durationRef.current)) || durationRef.current || duration || 0;
        const cur = (mediaElement && Number.isFinite(mediaElement.currentTime) ? mediaElement.currentTime : 0) || 0;
        window.dispatchEvent(new CustomEvent('app:player-progress', {
          detail: {
            currentTime: cur,
            duration: dur,
            playing: !!playing,
            hasTrack: !!currentMusic
          }
        }));
      } catch (_) {}
    }, 200);
    return () => {
      try { clearInterval(timer); } catch (_) {}
    };
  }, [playing, currentMusic, hasVideo, videoRef, audioRef, duration, durationRef]);

  const recordPlay = useCallback(async () => {
    await recordPlayHelper(currentMusic, axios, getUserId);
  }, [currentMusic]);

  const recordPlayProgress = useCallback(async (position, playDuration) => {
    await recordPlayProgressHelper(currentMusic, position, playDuration, axios, getUserId);
  }, [currentMusic]);

  const startPlayHistoryTracking = useCallback(() => {
    startPlayHistoryTrackingHelper(playHistoryIntervalRef, getActiveMediaElement, hasVideo, videoRef, audioRef, currentMusic, recordPlayProgress, PLAY_HISTORY_INTERVAL_MS);
  }, [currentMusic, hasVideo, recordPlayProgress]);

  const stopPlayHistoryTracking = useCallback(() => {
    stopPlayHistoryTrackingHelper(playHistoryIntervalRef);
  }, []);

  // 体验页播放控制桥接（注册一次，内部读 ref 取最新值）：
  // - app:player-toggle-play
  // - app:player-seek { detail: { pct?: number, time?: number } }
  // - app:player-play-url { detail: { url: string, loop?: boolean } }
  // - app:player-prime-play { detail: { music: object } }  // 用于“保留用户手势”的首播（首次进站更稳）
  const playerBridgeRef = useRef({
    currentMusic: null,
    playing: false,
    hasVideo: false,
    duration: 0
  });

  useEffect(() => {
    playerBridgeRef.current = {
      currentMusic,
      playing,
      hasVideo,
      duration,
      durationRef,
      audioRef,
      videoRef,
      progressFillRef,
      progressKnobRef,
      setContextCurrentMusic,
      addToPlaylist,
      getStreamUrl,
      resumeAudioContext,
      updatePlaying,
      startProgressTracking,
      stopProgressTracking,
      startPlayHistoryTracking,
      stopPlayHistoryTracking,
      togglePlayRef
    };
  }, [
    currentMusic,
    playing,
    hasVideo,
    duration,
    setContextCurrentMusic,
    addToPlaylist,
    getStreamUrl,
    resumeAudioContext,
    updatePlaying,
    startProgressTracking,
    stopProgressTracking,
    startPlayHistoryTracking,
    stopPlayHistoryTracking
  ]);

  useEffect(() => {
    const onToggle = () => {
      try {
        const st = playerBridgeRef.current || {};
        // 若当前有曲目：沿用原 togglePlay（含历史/歌词/淡入淡出等逻辑）
        if (st.currentMusic) {
          const fn = st.togglePlayRef && st.togglePlayRef.current;
          if (typeof fn === 'function') fn();
          return;
        }
        // 无曲目时：允许体验页播放/暂停“直设 URL”的音频（TUAC demo）
        const mediaEl = st.audioRef && st.audioRef.current;
        if (!mediaEl || !mediaEl.src) return;
        if (st.playing) {
          try { mediaEl.pause(); } catch (_) {}
          if (st.updatePlaying) st.updatePlaying(false);
          if (st.stopProgressTracking) st.stopProgressTracking();
          if (st.stopPlayHistoryTracking) st.stopPlayHistoryTracking();
        } else {
          // 注意：为了保留“用户手势”权限，尽量同步触发 play()，不要把 play 放进 then 链。
          try { if (st.resumeAudioContext) st.resumeAudioContext(); } catch (_) {}
          let p = null;
          try { p = mediaEl.play(); } catch (_) { p = null; }
          Promise.resolve(p).then(() => {
            if (st.updatePlaying) st.updatePlaying(true);
            if (st.startProgressTracking) st.startProgressTracking();
            if (st.startPlayHistoryTracking) st.startPlayHistoryTracking();
          }).catch(() => {});
        }
      } catch (_) {}
    };

    const onSeek = (e) => {
      try {
        const st = playerBridgeRef.current || {};
        const mediaElement = getActiveMediaElement(!!st.hasVideo, st.videoRef, st.audioRef);
        if (!mediaElement) return;
        const dur = mediaElement.duration || (st.durationRef && st.durationRef.current) || st.duration || 0;
        if (!dur || dur <= 0) return;
        const pctRaw = e && e.detail && typeof e.detail.pct === 'number' ? e.detail.pct : null;
        const timeRaw = e && e.detail && typeof e.detail.time === 'number' ? e.detail.time : null;
        let newTime = null;
        if (timeRaw !== null) newTime = timeRaw;
        else if (pctRaw !== null) newTime = pctRaw * dur;
        if (newTime === null) return;
        const clamped = Math.max(0, Math.min(dur, newTime));
        mediaElement.currentTime = clamped;
        // 同步进度条视觉（避免等待下一次 rAF/tick）
        const fill = st.progressFillRef && st.progressFillRef.current;
        const knob = st.progressKnobRef && st.progressKnobRef.current;
        if (fill && knob) {
          const pct = Math.max(0, Math.min(1, clamped / dur));
          fill.style.width = `${pct * 100}%`;
          knob.style.left = `${pct * 100}%`;
        }
      } catch (_) {}
    };

    const onPlayUrl = (e) => {
      try {
        const st = playerBridgeRef.current || {};
        const d = e && e.detail ? e.detail : null;
        const url = d && typeof d.url === 'string' ? d.url : '';
        if (!url) return;
        const loop = !!(d && d.loop);
        const mediaEl = st.audioRef && st.audioRef.current;
        if (!mediaEl) return;
        // 直设 URL：用于 TUAC demo（不污染曲库/playlist）
        try { mediaEl.pause(); } catch (_) {}
        mediaEl.loop = loop;
        mediaEl.src = url;
        try { mediaEl.load(); } catch (_) {}
        // 注意：为了保留“用户手势”权限，尽量同步触发 play()，不要把 play 放进 then 链。
        try { if (st.resumeAudioContext) st.resumeAudioContext(); } catch (_) {}
        let p = null;
        try { p = mediaEl.play(); } catch (_) { p = null; }
        Promise.resolve(p).then(() => {
          if (st.updatePlaying) st.updatePlaying(true);
          if (st.startProgressTracking) st.startProgressTracking();
          if (st.startPlayHistoryTracking) st.startPlayHistoryTracking();
        }).catch(() => {});
      } catch (_) {}
    };

    // 首次进站：从列表点“播放”时，外部常用 setPlaying(true) + 切歌，
    // 但真正的 play() 往往发生在异步 loadedmetadata 后，容易丢失用户手势而被 NotAllowedError 拦截。
    // 这里提供一个“用户手势保活”的同步入口：在点击事件里 dispatch 即可立即 play。
    const onPrimePlay = (e) => {
      try {
        const st = playerBridgeRef.current || {};
        const d = e && e.detail ? e.detail : null;
        const music = d && typeof d.music === 'object' ? d.music : null;
        if (!music) return;

        // 1) 同步更新 Context 的 currentMusic / playlist（避免依赖异步 effect）
        try { if (typeof st.addToPlaylist === 'function') st.addToPlaylist(music); } catch (_) {}
        try { if (typeof st.setContextCurrentMusic === 'function') st.setContextCurrentMusic(music); } catch (_) {}

        // 2) 同步设置 src 并立刻 play（尽量保持在同一用户手势任务里）
        const mediaEl = st.audioRef && st.audioRef.current;
        if (!mediaEl) return;
        const url = (typeof st.getStreamUrl === 'function') ? st.getStreamUrl(music, 'webm') : '';
        if (!url) return;
        try { mediaEl.pause(); } catch (_) {}
        try { mediaEl.currentTime = 0; } catch (_) {}
        try { mediaEl.src = url; } catch (_) {}
        try { mediaEl.load(); } catch (_) {}

        // 注意：为了保留“用户手势”权限，尽量同步触发 play()，不要把 play 放进 then/await 链。
        try { if (st.resumeAudioContext) st.resumeAudioContext(); } catch (_) {}
        let p = null;
        try { p = mediaEl.play(); } catch (_) { p = null; }
        Promise.resolve(p).then(() => {
          if (st.updatePlaying) st.updatePlaying(true);
          if (st.startProgressTracking) st.startProgressTracking();
          if (st.startPlayHistoryTracking) st.startPlayHistoryTracking();
        }).catch(() => {});
      } catch (_) {}
    };

    window.addEventListener('app:player-toggle-play', onToggle);
    window.addEventListener('app:player-seek', onSeek);
    window.addEventListener('app:player-play-url', onPlayUrl);
    window.addEventListener('app:player-prime-play', onPrimePlay);
    return () => {
      window.removeEventListener('app:player-toggle-play', onToggle);
      window.removeEventListener('app:player-seek', onSeek);
      window.removeEventListener('app:player-play-url', onPlayUrl);
      window.removeEventListener('app:player-prime-play', onPrimePlay);
    };
  }, []);

  useExternalPlayingSync({
    externalPlaying,
    currentMusic,
    playing,
    isLoadingRef,
    getVideoFilePath,
    getActiveMediaElement,
    videoRef,
    audioRef,
    updatePlaying,
    resumeAudioContext,
    isProgressTrackingRef,
    playHistoryIntervalRef,
    startProgressTracking,
    startPlayHistoryTracking,
    recordPlay,
    applyAudioFocusMode,
    stopProgressTracking,
    stopPlayHistoryTracking
  });

  progressTrackingRefsRef.current = {
    playerRef,
    audioRef,
    videoRef,
    progressRafRef,
    progressTrackRef,
    progressFillRef,
    progressKnobRef,
    durationRef,
    currentPlayingFormatRef,
    isSeekingRef,
    isProgressTrackingRef,
    seekPointerIdRef,
    seekWasPlayingRef,
    seekRecoverTokenRef,
    abLoopRef,
    lyricsRef,
    currentLyricIndexRef,
    currentLyricLineRef,
    lastLyricSyncMsRef,
    lastTimeLabelSecRef,
    lyricOffsetRef,
    seekPreviewRef,
    seekPreviewTimeRef,
    seekPreviewTextRef,
    seekPreviewTransRef,
    seekPreviewTextAnimRef,
    seekPreviewTransAnimRef,
    seekPreviewRafRef,
    seekPreviewXRef,
    seekPreviewTargetXRef,
    seekPreviewVisibleRef,
    seekPreviewHideTimerRef,
    seekPreviewLastTargetXRef,
    seekPreviewLastTsRef,
    seekPreviewVelRef,
    currentSeekPreviewBoxRef,
    seekPreviewLyricIdxRef,
    progressTrackRectRef,
    progressTrackRectTimestampRef,
    coverFadeOutStartedRef,
    coverActiveLayerRef,
    currentTimeTextRefDesktop,
    currentTimeTextRefMobile,
    progressIntervalRef
  };
  progressTrackingCallbacksRef.current = {
    applyLyricHighlight,
    updateMiniLyric,
    setCoverLayerAOpacity,
    setCoverLayerBOpacity,
    hasVideo,
    hasVideoInCurrentMusic,
    shouldShowCoverWindow,
    visualFadeMs,
    fadeInOut,
    lyricSettings,
    // seek 后恢复/兜底需要这些能力
    playing,
    resumeAudioContext,
    rebuildAudioGraph
  };
  loadMusicRefsRef.current = {
    audioRef,
    videoRef,
    wasmDecodeAbortRef,
    wasmAudioUrlRef,
    lastUiTickRef,
    currentLyricIndexRef,
    isLoadingRef,
    durationRef,
    wasCachedRef,
    isProgressTrackingRef,
    playHistoryIntervalRef,
    activeMediaTypeRef,
    currentPlayingFormat,
    muted,
    volume,
    fadeInOut
  };
  loadMusicSettersRef.current = {
    setLoading,
    setDuration,
    updatePlaying,
    setCurrentPlayingFormat,
    setVideoDimensions,
    setIsVideoFullscreen,
    setIsCached,
    setLyrics,
    setVisualFadeInToken,
    setIsVisualSwitching,
    clearLyricHighlight,
    updateMiniLyric
  };
  loadMusicCallbacksRef.current = {
    getVideoFilePath,
    getCachedMusicMetadata,
    isFileCached,
    getMusicStreamUrl,
    getStreamUrl,
    rebuildAudioGraph,
    resumeAudioContext,
    loadLyrics,
    startProgressTracking,
    startPlayHistoryTracking,
    recordPlay,
    cacheMusicMetadata,
    cacheMusicFile,
    getCoverStreamUrl: getCoverStreamUrlLocal,
    getFadeStep,
    FADE_TICK_MS,
    isAdmin,
    t,
    showUiHint,
    showHintPlus,
    mpWarn,
    applyAudioFocusMode
  };

  const { handlePrevious, handleNext, togglePlayMode } = usePlayerPlaylistHandlers({
    playlist,
    currentMusic,
    playMode,
    onNext,
    onPrevious,
    fadeInOut,
    getVideoFilePath,
    getActiveMediaElement,
    videoRef,
    audioRef,
    playing,
    muted,
    fadeOutTimerRef,
    setIsVisualSwitching,
    setVisualFadeOutToken,
    setPlayMode
  });

  // 设置进度（未使用，保留用于未来功能）
  // eslint-disable-next-line no-unused-vars
  const handleSeek = (e) => {
    const mediaElement = getActiveMediaElement(hasVideo, videoRef, audioRef);
    if (!mediaElement) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const dur = mediaElement.duration || durationRef.current || duration || 0;
    const newTime = percentage * dur;
    
    mediaElement.currentTime = newTime;
    // 不再 setState 更新 currentTime（避免播放时额外重渲染）；时间显示由 rAF 直写 DOM
    // 立刻更新进度条视觉（避免等待下一帧/下一次 tick）
    requestAnimationFrame(() => {
      const dur2 = mediaElement?.duration || durationRef.current || 0;
      if (!dur2 || dur2 <= 0) return;
      const pct = Math.max(0, Math.min(1, newTime / dur2));
      updateAllProgressVisual(pct);
    });
  };

  // 设置音量
  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    // 如果有视频，也设置视频音量
    if (hasVideo && videoRef.current) {
      videoRef.current.volume = newVolume;
    }
    setMuted(newVolume === 0);
  };

  // 切换静音
  const toggleMute = () => {
    const mediaElement = getActiveMediaElement(hasVideo, videoRef, audioRef);
    if (mediaElement) {
      if (muted) {
        const newVolume = volume || 0.5;
        mediaElement.volume = newVolume;
        if (audioRef.current) audioRef.current.volume = newVolume;
        if (hasVideo && videoRef.current) videoRef.current.volume = newVolume;
        setMuted(false);
      } else {
        mediaElement.volume = 0;
        if (audioRef.current) audioRef.current.volume = 0;
        if (hasVideo && videoRef.current) videoRef.current.volume = 0;
        setMuted(true);
      }
    }
  };

  // 清理
  useEffect(() => {
    return () => {
      stopProgressTracking();
      stopPlayHistoryTracking();
      if (seekPreviewHideTimerRef.current) {
        clearTimeout(seekPreviewHideTimerRef.current);
        seekPreviewHideTimerRef.current = null;
      }
      if (seekPreviewRafRef.current) {
        cancelAnimationFrame(seekPreviewRafRef.current);
        seekPreviewRafRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // durationRef 同步最新 duration，避免 rAF 闭包读到旧值
  useEffect(() => {
    durationRef.current = duration || 0;
  }, [duration]);

  // duration/切歌时同步一次进度条视觉，避免初始停在 0%
  useEffect(() => {
    const audio = audioRef.current;
    const fill = progressFillRef.current;
    const knob = progressKnobRef.current;
    const dur = audio?.duration || durationRef.current || 0;
    if (!audio || !fill || !knob || !dur || dur <= 0) return;
    const pct = Math.max(0, Math.min(1, audio.currentTime / dur));
    fill.style.width = `${pct * 100}%`;
    knob.style.left = `${pct * 100}%`;
  }, [duration, currentMusic?.id]);

  // 播放卡死/自动停止：watchdog + media 事件触发恢复（不需要切歌）
  useEffect(() => {
    if (!playing || !currentMusic) return;
    const hasVideo = !!getVideoFilePath(currentMusic);
    const mediaEl = getActiveMediaElement(hasVideo, videoRef, audioRef);
    if (!mediaEl) return;

    const state = playbackStallStateRef.current;
    state.lastTime = mediaEl.currentTime ?? 0;
    state.lastAdvanceMs = Date.now();

    const tick = () => {
      try {
        if (!playing) return;
        // ended 由 ended handler 处理
        if (mediaEl.ended) return;
        // 某些平台会在后台/省电策略下把媒体“自动暂停”，但 UI 仍处于 playing；此时尝试恢复
        if (mediaEl.paused) {
          recoverStuckPlayback('paused-while-playing').catch(() => {});
          return;
        }

        const t = mediaEl.currentTime ?? 0;
        const now = Date.now();
        if (t > (state.lastTime + 0.06)) {
          state.lastTime = t;
          state.lastAdvanceMs = now;
          return;
        }
        const stuckFor = now - (state.lastAdvanceMs || 0);
        if (stuckFor > 3500) {
          recoverStuckPlayback(`watchdog(stuckFor=${stuckFor})`).catch(() => {});
          // 防抖：避免下一秒又触发
          state.lastAdvanceMs = now;
        }
      } catch (_) {}
    };

    const timerId = setInterval(tick, 1000);
    const onProblem = (e) => {
      // 等待一小段时间，给浏览器自己恢复的机会
      setTimeout(() => {
        try {
          if (!playing) return;
          if (mediaEl.ended || mediaEl.paused) return;
          recoverStuckPlayback(`event:${e?.type || 'unknown'}`).catch(() => {});
        } catch (_) {}
      }, 1500);
    };
    mediaEl.addEventListener('stalled', onProblem);
    mediaEl.addEventListener('waiting', onProblem);
    mediaEl.addEventListener('error', onProblem);

    return () => {
      clearInterval(timerId);
      mediaEl.removeEventListener('stalled', onProblem);
      mediaEl.removeEventListener('waiting', onProblem);
      mediaEl.removeEventListener('error', onProblem);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, currentMusic?.id, hasVideoInCurrentMusic, recoverStuckPlayback]);

  // 音频结束事件
  useEffect(() => {
    const mediaElement = getActiveMediaElement(hasVideo, videoRef, audioRef);
    if (!mediaElement) return;

    const handleEnded = createHandleEnded({
      mediaElement,
      currentMusic,
      duration,
      playlist,
      playMode,
      playlistLoop,
      hasVideo,
      fadeInOut,
      isSingleCoverMode,
      muted,
      volume,
      videoRef,
      audioRef,
      coverFadeOutStartedRef,
      coverActiveLayerRef,
      getUserId,
      axios,
      stopProgressTracking,
      stopPlayHistoryTracking,
      updatePlaying,
      setContextPlaying,
      setShowLyrics,
      handleNext,
      startProgressTracking,
      startPlayHistoryTracking,
      setCoverLayerAOpacity,
      setIsVisualSwitching,
      setVisualFadeInToken
    });

    mediaElement.addEventListener('ended', handleEnded);
    return () => mediaElement.removeEventListener('ended', handleEnded);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMusic, duration, playMode, playlist, playlistLoop, handleNext]);

  // 下载音乐（打开下载对话框）
  const handleDownload = () => {
    if (!currentMusic) return;
    
    if (!currentMusic.allow_download) {
      alert(t('musicList.downloadNotAllowed'));
      return;
    }
    
    setDownloadDialogOpen(true);
  };

  // 确认下载（从对话框）
  const handleDownloadConfirm = async (formats, includeLyrics) => {
    if (!currentMusic) return;
    
    // 使用统一的下载函数
    await downloadMusic(currentMusic.id, currentMusic.allow_download, null, formats, includeLyrics);
  };

  // 分享音乐
  const handleShare = async () => {
    if (!currentMusic) return;
    
    // 获取当前语言（从 i18n 或 URL 路径）
    const seg0 = window.location.pathname
      .split('/')
      .filter(Boolean)
      .find((seg) => ['cn', 'tw', 'ja', 'en', ZH_CN, ZH_TW, ZH_CN_LC, ZH_TW_LC].includes(seg));
    const segCanonical =
      seg0 === ZH_CN || seg0 === ZH_CN_LC ? 'cn' :
      seg0 === ZH_TW || seg0 === ZH_TW_LC ? 'tw' :
      seg0;
    const currentLang = i18n?.language || segCanonical || 'cn';
    
    // 使用统一的分享函数（复制链接时显示 Hint）
    const shareResult = await shareMusic(currentMusic.id, currentLang, null, {
      onCopyHint: () => showUiHint(t('hints.copied'), { duration: 1500 })
    });
    if (!shareResult) return;
    if (shareResult.usedWebShare || shareResult.isMobile) return;
    const pageMeta = getShareMeta();
    setShareDialogMeta(mergeShareMeta(pageMeta, shareResult?.shareInfo));
    setShareDialogOpen(true);
  };

  const handleShareCopy = (url) => {
    if (!url || !currentMusic) return;
    copyToClipboard(url, () => {
      showUiHint(t('hints.copied'), { duration: 1500 });
    });
  };

  const formatFileSize = (bytes) => formatFileSizeRaw(bytes, t('player.unknown'));

  // 格式化码流/采样率（抽离到独立模块；这里保留 i18n 的 unknown 文案）
  const formatBitrate = (bitrate) => formatBitrateRaw(bitrate, t('player.unknown'));
  const formatSampleRate = (sampleRate) => formatSampleRateRaw(sampleRate, t('player.unknown'));

  if (!currentMusic) {
    return (
      <DockBar
        playing={playing}
        isFullscreen={false}
        autoHidden={autoHidden}
        manualHidden={manualHidden}
        title={null}
        album={''}
        hasVideo={false}
        isInstrumental={false}
        onTogglePlay={null}
        onBump={bumpAutoHideCountdown}
        setAutoHidden={setAutoHidden}
        setManualHidden={setManualHidden}
        isEmpty
        onOpenEmpty={() => {
          // 无音乐时：为了有“滑动弹出”的动画，需要让播放器先以 hidden 状态挂载，再在下一帧展开。
          // 这里不直接 setManualHidden(false)，而是：
          // 1) 先 setManualHidden(true)（确保初始 transform=translateY(110%)）
          // 2) 选中一首歌触发播放器挂载
          // 3) 下一帧 setManualHidden(false) 触发滑入动画
          try { setAutoHidden(false); } catch (_) {}
          try { setManualHidden(true); } catch (_) {}
          try { setShowVideoWindow(true); } catch (_) {}

          const first = Array.isArray(playlist) && playlist.length > 0 ? playlist[0] : null;
          if (first && typeof first === 'object') {
            try { userInitiatedChangeRef.current = true; } catch (_) {}
            try {
              if (setContextCurrentMusic) setContextCurrentMusic(first);
              else if (onPlaylistChange) onPlaylistChange(first, 0);
            } catch (_) {}
            // 下一帧展开，触发滑入动画
            try {
              requestAnimationFrame(() => {
                try { setManualHidden(false); } catch (_) {}
              });
            } catch (_) {
              try { setManualHidden(false); } catch (_) {}
            }
            return;
          }

          // 播放列表也为空：不要跳转页面（会导致“滚到顶”），改为显示一个占位播放器
          // 这样用户可以继续操作播放器按钮（如打开设置/播放列表）或从页面其他位置选择音乐。
          try {
            const placeholder = {
              id: null,
              title: t('player.noMusicSelected', '未选择音乐'),
              title_zh_cn: t('player.noMusicSelected', '未选择音乐'),
              title_zh_tw: t('player.noMusicSelected', '未选择音乐'),
              title_ja: t('player.noMusicSelected', '未选择音乐'),
              title_en: t('player.noMusicSelected', '未选择音乐'),
              album: '',
              file_path: null,
              file_format: null,
              allow_download: false,
              lyrics_type: null,
              language_category: null,
              is_public: true
            };
            if (setContextCurrentMusic) setContextCurrentMusic(placeholder);
            // 确保不处于播放状态
            try { setContextPlaying(false); } catch (_) {}
          } catch (_) {}

          // 下一帧展开，触发滑入动画
          try {
            requestAnimationFrame(() => {
              try { setManualHidden(false); } catch (_) {}
            });
          } catch (_) {
            try { setManualHidden(false); } catch (_) {}
          }
        }}
      />
    );
  }

  if (typeof document === 'undefined') return null;

  const commonPanelProps = {
    t,
    i18n,
    cx,
    spaNavigate,
    showUiHint,
    devViewport,
    videoWindowStyle,
    showVideoWindow,
    setShowVideoWindow,
    setIsVideoFullscreen,
    setIsFullscreen,
    videoRef,
    miniLyricTextRef,
    miniLyricTransTextRef,
    currentMusic,
    playing,
    loading,
    duration,
    isFullscreen,
    isVideoFullscreen,
    isMobileViewport,
    isMdToLg,
    isLgUp,
    hideVolumeSlider,
    isMinimized,
    hasVideoInCurrentMusic,
    hasVideo,
    autoHidden,
    setAutoHidden,
    setManualHidden,
    isCached,
    showFileInfo,
    setShowFileInfo,
    fileInfoPosition,
    setFileInfoPosition,
    currentPlayingFormat,
    getCoverStreamUrlLocal,
    getCurrentLanguageTitle,
    formatFileSize,
    formatBitrate,
    formatSampleRate,
    formatTime,
    handleButtonClickWithHint,
    handleDownload,
    handleShare,
    ensureVideoMediaInfo,
    handleDownloadConfirm,
    downloadDialogOpen,
    setDownloadDialogOpen
  };
  const progressProps = {
    currentTimeTextRefDesktop,
    currentTimeTextRefMobile,
    progressTrackRef,
    progressFillRef,
    progressKnobRef,
    seekPreviewRef,
    seekPreviewTimeRef,
    seekPreviewTextRef,
    seekPreviewTransRef,
    handleSeekPointerDown,
    handleSeekPointerMove,
    handleSeekPointerUpOrCancel
  };
  const transportProps = {
    handlePrevious,
    handleNext,
    togglePlay,
    playMode,
    togglePlayMode,
    muted,
    volume,
    handleVolumeChange,
    toggleMute
  };
  const settingsProps = {
    showSettings,
    setShowSettings,
    settingsButtonRef,
    settingsPanelRef,
    volumeSliderWrapRef,
    desktopControlsRowRef,
    sleepTimer,
    sleepTimerMinutes,
    setSleepTimerHandler,
    fadeInOut,
    setFadeInOut,
    useWasmOpusDecode,
    setUseWasmOpusDecode,
    stabilityMode,
    setStabilityMode,
    wasmSimdSupported,
    surroundEnabled,
    setSurroundEnabled: handleSetSurroundEnabled,
    surroundIntensity,
    setSurroundIntensity,
    distortionEnabled,
    setDistortionEnabled,
    distortionIntensity,
    setDistortionIntensity,
    bassLevel,
    setBassLevel,
    reverbLevel,
    setReverbLevel,
    environmentMode,
    setEnvironmentMode,
    audioEnginePreset,
    setAudioEnginePreset,
    spaceModeEnabled,
    setSpaceModeEnabled,
    bassEnabled,
    setBassEnabled,
    audioFocusMode,
    setAudioFocusMode,
    isMobileDevice
  };
  const lyricsProps = {
    showLyrics,
    setShowLyrics,
    shouldShowMiniLyrics,
    lyrics,
    lyricOffset,
    lyricsViewportHeight,
    lyricSettings,
    setLyricSettings,
    highlightTheme,
    lyricsContainerRef,
    lyricLineRefs,
    currentLyricLineRef
  };
  const playlistProps = {
    showPlaylist,
    setShowPlaylist,
    playlist,
    playlistLoop,
    setPlaylistLoop,
    playlistPanelRef,
    onPlaylistChange: handlePlaylistChange,
    onRemoveFromPlaylist: handleRemoveFromPlaylist,
    setPlaylist: handleSetPlaylist
  };

  const panelContextValue = { ...commonPanelProps, ...progressProps, ...transportProps, ...settingsProps, ...lyricsProps, ...playlistProps };

  return (
    <>
      <SeekPreviewPortal
        refs={{
          seekPreviewRef,
          seekPreviewTimeRef,
          seekPreviewTextRef,
          seekPreviewTransRef
        }}
      />
      <PlayerPanelContext.Provider value={panelContextValue}>
        <PlayerPortals
          playerRef={playerRef}
          audioRef={audioRef}
          isFullscreen={isFullscreen}
          playing={playing}
          autoHidden={autoHidden}
          manualHidden={manualHidden}
          isLoading={loading}
          onMouseMove={bumpAutoHideCountdown}
          onMouseEnter={() => setIsPlayerHovering(true)}
          onMouseLeave={() => setIsPlayerHovering(false)}
          onBlankClick={handlePlayerBlankClick}
          videoWindowProps={{
            coverLayerAUrl,
            coverLayerBUrl,
            coverLayerAType,
            coverLayerBType,
            coverLayerAOpacity,
            coverLayerBOpacity,
            coverLayerANoFadeIn,
            coverLayerBNoFadeIn,
            showCover: !!(coverUrl || nextCoverUrl),
            visualFadeMs,
            visualFadeOutToken,
            visualFadeInToken,
            shouldShowVideoWindow,
            videoDockStyle,
            videoWindowStyle,
            dockingReady,
            isVideoFullscreen,
            setIsVideoFullscreen,
            shouldShowVideoSubtitles,
            videoRef,
            miniLyricTextRef,
            miniLyricTransTextRef,
            autoHidden,
            setAutoHidden,
            showPlaylist,
            setShowPlaylist,
            setShowVideoWindow
          }}
          panelProps={panelContextValue}
        dockProps={{
          playing,
          isFullscreen,
          autoHidden,
          manualHidden,
          title: getCurrentLanguageTitle(currentMusic),
          album: currentMusic?.album || '',
          hasVideo: hasVideoInCurrentMusic,
          isInstrumental: isInstrumentalMusic(currentMusic),
          onTogglePlay: togglePlay,
          onBump: bumpAutoHideCountdown,
          setAutoHidden,
          setManualHidden
        }}
        />
      </PlayerPanelContext.Provider>
      <ShareDialog
        open={shareDialogOpen}
        onClose={() => {
          setShareDialogOpen(false);
          setShareDialogMeta(null);
        }}
        meta={shareDialogMeta}
        onCopy={handleShareCopy}
      />
    </>
  );
};

export default MusicPlayer;

